<template>
    <form
        @submit="onSubmit($event)"
        novalidate
    >
        <AlertNoEmailFound
            v-if="emailNotExists"
            class="mb-8"
        ></AlertNoEmailFound>

        <v-row>
            <v-col cols="12">
                <v-text-field
                    v-model="email"
                    v-bind="emailProps"
                    type="email"
                    name="email"
                    label="Email"
                    autocorrect="off"
                    autocapitalize="none"
                    autocomplete="username"
                    variant="outlined"
                    hide-details="auto"
                ></v-text-field>
            </v-col>
            <v-col cols="12">
                <v-text-field
                    v-model="password"
                    v-bind="passwordProps"
                    name="password"
                    label="Password"
                    autocomplete="current-password"
                    :type="showPassword ? 'text' : 'password'"
                    :append-inner-icon="showPassword ? mdiEyeOff : mdiEye"
                    @click:append-inner="showPassword = !showPassword"
                    variant="outlined"
                    hide-details="auto"
                ></v-text-field>
            </v-col>
            <v-col cols="12">
                <v-btn
                    variant="elevated"
                    color="primary"
                    type="submit"
                    :disabled="!(formMeta.dirty || formMeta.touched) || !formMeta.valid"
                    :loading="isLoading"
                    size="x-large"
                    block
                >
                    <template #loader>
                        <v-progress-circular indeterminate></v-progress-circular>
                    </template>
                    Sign In
                </v-btn>
            </v-col>
        </v-row>

        <div class="
            d-flex
            flex-column
            gap-3
            align-center
            align-sm-end
            text-subtitle-1
            font-weight-bold
            text-left
            pl-0
            mt-4
        ">
            <NuxtLink
                to="/forgot-password"
                class="text-secondary"
            >
                Forgot your password?
            </NuxtLink>
            <NuxtLink
                to="/create-account"
                class="text-secondary"
            >
                No account? Create one now.
            </NuxtLink>
        </div>
    </form>
</template>

<script lang="ts" setup>
    import { toTypedSchema } from '@vee-validate/valibot';
    import type { SignInResponse } from '~/types';
    import { SignInModel } from '~/models';
    import { useFormValidation, useGlobalAlert } from 'x1-base/composables';
    import {
        useUser,
        useTracking,
        useErrorMessage,
        useCustomAuth
    } from '~/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import { mdiEyeOff, mdiEye } from '@mdi/js';
    import AlertNoEmailFound from '~/components/alerts/AlertNoEmailFound/AlertNoEmailFound.vue';

    const {
        getRedirect,
        clearRedirect
    } = useCustomAuth();
    const useUserObj = useUser();
    const createGlobalAlert = useGlobalAlert();
    const useTrackingObj = useTracking();
    const showPassword = ref<boolean>(false);
    const isLoading = ref(false);
    const { getDefineFieldOptions } = useFormValidation();
    const emailNotExists = ref<boolean>(false);

    const {
        handleSubmit,
        defineField,
        meta: formMeta
    } = useForm({
        validationSchema: toTypedSchema(
            SignInModel
        ),
        initialValues: {
            email: '',
            password: ''
        }
    });

    const [email, emailProps] = defineField('email', getDefineFieldOptions('vuetify'));
    const [password, passwordProps] = defineField('password', getDefineFieldOptions('vuetify'));

    const onSubmit = handleSubmit(async (values) => {
        emailNotExists.value = false;
        isLoading.value = true;
        const response: SignInResponse | Error = await useUserObj.signIn(values);
        isLoading.value = false;

        if (!lo_isError(response)) {
            if (response.user) {
                const redirectObj = getRedirect();
                clearRedirect();
                useTrackingObj.track('login', {
                    method: 'email'
                });

                await navigateTo(redirectObj);
            }

            return;
        } else if (response?.message?.includes('404')) {
            emailNotExists.value = true;
            return;
        } else if (response?.message?.includes('406')) {
            await navigateTo({ path: '/set-password', query: { migration: 'yes' } });
            return;
        }

        handleLoginError(response);

    });

    function handleLoginError(loginErr?: Error) {
        createGlobalAlert({
            text: useErrorMessage('globalAlert.signUp.invalidCredentials'),
            show: true,
            type: 'error'
        });
    }
</script>

<style lang="scss" scoped></style>
